// The anatomy tool's intake, and the pipeline every front door feeds.
//
// ---------------------------------------------------------------------------
// WHY THIS IS A FILE AND NOT A <script> BLOCK, 2026-08-25
// ---------------------------------------------------------------------------
// The same two mechanisms that killed the generator's inline script (I-83) apply
// here unchanged, and the reasoning is written out in full on `tool.js`. The
// short version: the page is `ServerPrerendered`, so a script that runs at parse
// time is left holding nodes Blazor has already replaced, and inline script TEXT
// is re-inserted in fragments and throws out of `appendChild`. So: an external
// file, listeners on `document` which is never replaced, elements looked up at
// use time, and a guard that makes a second execution a no-op.
//
// ---------------------------------------------------------------------------
// WHY EVERYTHING HAPPENS HERE AND NOTHING IS UPLOADED
// ---------------------------------------------------------------------------
// The image never leaves the browser. That is the product's claim and it is also
// why this page costs the box nothing: no upload endpoint, no abuse surface, no
// egress, and a page that is a static asset with a script. Anything that would
// send bytes to the server breaks the claim on the page, so it does not happen
// here without the copy changing too.
//
// ---------------------------------------------------------------------------
// NO CLASS NAMES IN THIS FILE
// ---------------------------------------------------------------------------
// Tailwind generates only the classes it can SEE, and it does not scan this
// file. A literal utility class written here would be purged from app.css and
// the style would simply not exist, on a green build - I-80. So the markup hands
// the two drag states over as `data-idle-class` and `data-active-class`, and
// this file only ever moves strings it was given.

import jsQR from './vendor/jsqr/jsQR.js';
import { sampleGrid, classify, dataPath, gridLooksRight } from './grid.js';
import { render as renderXray, layers } from './xray.js';

(function () {
    'use strict';

    if (window.__qrAnatomy) { return; }
    window.__qrAnatomy = true;

    var DROP = 'anatomy-drop';
    var FILE = 'anatomy-file';
    var CAMERA = 'anatomy-camera';
    var RESULT = 'anatomy-result';
    var INTAKE = 'anatomy-intake';

    function byId(id) { return document.getElementById(id); }

    // -----------------------------------------------------------------------
    // One pipeline, four front doors.
    //
    // Drop, paste, file picker and camera all end in the same place: a bitmap
    // this function can read. Only the camera adds a permission and a loop, and
    // it is deliberately the only one that asks for anything.
    // -----------------------------------------------------------------------
    // -----------------------------------------------------------------------
    // THE TEARDOWN
    //
    // **jsQR does detection and nothing above it.** It finds the finder
    // patterns, works out the version, corrects the perspective and samples the
    // grid, which is the hard, boring, well-solved half. Everything shown here
    // is read off what it returns or worked out below.
    //
    // **THE ANSWER GETS THE PAGE.** The reader dropped a code in to find out
    // what it DOES. The version and the segment count are a second thing behind
    // their own heading, smaller, and skippable. The first version of this had
    // that backwards.
    //
    // **NO CLASS NAMES FROM TAILWIND.** This markup does not exist at build
    // time, so a utility written here would be purged and simply not exist
    // (I-80). The `.anatomy-*` rules are hand-written in app.css, the same
    // forced exception `.blog-prose` is.
    // -----------------------------------------------------------------------

    function element(tag, className, text) {
        var node = document.createElement(tag);
        if (className) { node.className = className; }
        if (text !== undefined) { node.textContent = text; }
        return node;
    }

    function fieldList(className, pairs) {
        var list = element('dl', className);
        pairs.forEach(function (pair) {
            if (pair[1] === undefined || pair[1] === null || pair[1] === '') { return; }

            var term = element('dt', null);
            // **The icon comes from the sprite the PAGE rendered**, never from
            // data in here: the vendored files are embedded resources and
            // copying their paths into this bundle would let two copies drift.
            // See the sprite comment in Anatomy.razor.
            if (pair[2]) { term.appendChild(icon(pair[2])); }
            term.appendChild(document.createTextNode(pair[0]));

            list.appendChild(term);
            list.appendChild(element('dd', null, pair[1]));
        });
        return list;
    }

    function icon(name) {
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', 'anatomy-icon');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');

        var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        // `href` rather than `xlink:href`: the latter is deprecated and this
        // page has no reason to support a browser that needs it.
        use.setAttribute('href', '#anatomy-icon-' + name);
        svg.appendChild(use);
        return svg;
    }

    // Key/value payloads (WiFi, MeCard) escape their separators with a
    // backslash, so a naive split on ; or : loses any value containing one.
    function unescapeValue(text) {
        return text.replace(/\\(.)/g, '$1');
    }

    function taggedValue(text, tag) {
        var match = new RegExp(tag + ':((?:\\\\.|[^;])*)', 'i').exec(text);
        return match ? unescapeValue(match[1]) : '';
    }

    function lineValue(text, tag) {
        var match = new RegExp('^' + tag + '[^:\\r\\n]*:(.*)$', 'im').exec(text);
        return match ? match[1].trim() : '';
    }

    // **What the code DOES, taken apart rather than shown raw.** A WiFi code is
    // a network name and a security type, not `WIFI:S:...;T:WPA;P:...;;`.
    // Anything unrecognised stays plain text rather than being guessed at,
    // because a wrong label is worse than no label.
    function describe(text) {
        if (/^WIFI:/i.test(text)) {
            var security = taggedValue(text, 'T') || 'nopass';
            return {
                kind: 'A WiFi network',
                target: taggedValue(text, 'S'),
                fields: [
                    ['Network name', taggedValue(text, 'S')],
                    ['Security', security.toLowerCase() === 'nopass' ? 'Open, no password' : security.toUpperCase()],
                    ['Password', taggedValue(text, 'P')],
                    ['Hidden', /^(true|1)$/i.test(taggedValue(text, 'H')) ? 'Yes' : ''],
                ],
            };
        }

        if (/^BEGIN:VCARD/i.test(text)) {
            return {
                kind: 'A contact card',
                target: lineValue(text, 'FN') || lineValue(text, 'N'),
                fields: [
                    ['Name', lineValue(text, 'FN') || lineValue(text, 'N')],
                    ['Organisation', lineValue(text, 'ORG')],
                    ['Phone', lineValue(text, 'TEL')],
                    ['Email', lineValue(text, 'EMAIL')],
                    ['Website', lineValue(text, 'URL')],
                ],
            };
        }

        if (/^MECARD:/i.test(text)) {
            return {
                kind: 'A contact card',
                target: taggedValue(text, 'N'),
                fields: [
                    ['Name', taggedValue(text, 'N')],
                    ['Phone', taggedValue(text, 'TEL')],
                    ['Email', taggedValue(text, 'EMAIL')],
                ],
            };
        }

        if (/BEGIN:VEVENT/i.test(text)) {
            return {
                kind: 'A calendar event',
                target: lineValue(text, 'SUMMARY'),
                fields: [
                    ['Event', lineValue(text, 'SUMMARY')],
                    ['Starts', lineValue(text, 'DTSTART')],
                    ['Ends', lineValue(text, 'DTEND')],
                    ['Where', lineValue(text, 'LOCATION')],
                ],
            };
        }

        if (/^mailto:/i.test(text)) {
            var address = text.slice(7).split('?')[0];
            return { kind: 'An email address', target: decodeURIComponent(address), fields: [] };
        }

        if (/^tel:/i.test(text)) {
            return { kind: 'A phone number', target: text.slice(4), fields: [] };
        }

        if (/^smsto:/i.test(text)) {
            var parts = text.slice(6).split(':');
            return {
                kind: 'A text message',
                target: parts[0],
                fields: [['Message', parts.slice(1).join(':')]],
            };
        }

        if (/^geo:/i.test(text)) {
            var point = text.slice(4).split(',');
            return {
                kind: 'A location',
                target: text.slice(4),
                fields: [['Latitude', point[0]], ['Longitude', point[1]]],
            };
        }

        if (/^https?:\/\//i.test(text)) {
            // **The domain is the field that matters**, because it is the one
            // thing that tells somebody whether a code goes where its poster
            // claims. Shown separately rather than left inside a long URL.
            var host = '';
            try { host = new URL(text).hostname; } catch (ignored) { host = ''; }
            return {
                kind: 'A link',
                target: text,
                fields: [['Goes to', host]],
            };
        }

        return { kind: 'Plain text', target: text, fields: [] };
    }

    // The module count follows from the version and nothing else: 21x21 at
    // version 1, four more per side per version, up to 177 at 40.
    function modulesPerSide(version) { return (version * 4) + 17; }

    // **The data modes, folded into the plate rather than tabled below it.**
    // A table of segments restated the character count and the segment count
    // that the plate already gives, which is the same numbers twice and a
    // heavier thing to read.
    //
    // `eci` is filtered out because it is not a data mode: it declares a
    // character set. Listing it beside `byte` would read as a second encoding.
    function modesOf(chunks) {
        var modes = [];
        chunks.forEach(function (chunk) {
            if (chunk.type === 'eci') { return; }
            if (modes.indexOf(chunk.type) === -1) { modes.push(chunk.type); }
        });
        return modes.join(', ');
    }

    // **The failure path is the product.** A normal decoder says "no QR code
    // found", which tells somebody holding a failed print run nothing at all.
    function failure(container, size) {
        var box = element('div', 'anatomy-failure');
        box.appendChild(element('h2', null, 'No QR code found in that picture'));
        box.appendChild(element('p', null,
            'The picture came through at ' + size.width + ' by ' + size.height
            + ' pixels, and nothing in it had the three corner squares a reader '
            + 'looks for first. The usual causes are the code being small in the '
            + 'frame, too little contrast between the dark and light squares, the '
            + 'pale border around it being cropped away, or a photograph taken at '
            + 'too steep an angle. Only one code is read per picture, so several '
            + 'in one photograph means this found none rather than choosing.'));
        container.appendChild(box);
    }

    // -----------------------------------------------------------------------
    // THE CAMERA
    //
    // **Asked for only on a deliberate click**, which is a correctness decision
    // rather than a courtesy: a denied permission is sticky, so prompting
    // somebody who did not ask does not annoy them once, it disables the
    // feature for them permanently with no friendly way back.
    //
    // **The track is stopped on every exit path.** A camera light left on after
    // the reader has moved on is the worst possible look for a page whose whole
    // claim is that nothing leaves the browser. Stopped on a successful read, on
    // Stop, on the tab being hidden, and on leaving the page.
    // -----------------------------------------------------------------------

    var camera = { stream: null, timer: null, view: null };

    function stopCamera() {
        if (camera.timer) { window.clearInterval(camera.timer); camera.timer = null; }

        if (camera.stream) {
            camera.stream.getTracks().forEach(function (track) { track.stop(); });
            camera.stream = null;
        }

        if (camera.view) { camera.view.remove(); camera.view = null; }

        var button = byId(CAMERA);
        if (button) { button.setAttribute('aria-expanded', 'false'); }
    }

    function cameraMessage(error) {
        if (!error) { return 'The camera could not be started.'; }
        if (error.name === 'NotAllowedError') {
            return 'Permission for the camera was refused. Browsers remember that, '
                + 'so turning it back on is done in the site settings rather than here.';
        }
        if (error.name === 'NotFoundError' || error.name === 'OverconstrainedError') {
            return 'No camera was found on this device.';
        }
        if (error.name === 'NotReadableError') {
            return 'The camera is in use by something else.';
        }
        return 'The camera could not be started: ' + error.name + '.';
    }

    function startCamera() {
        stopCamera();

        var intake = byId(INTAKE);
        if (!intake) { return; }

        var view = element('div', 'anatomy-camera-view');
        var video = document.createElement('video');
        video.className = 'anatomy-video';
        video.setAttribute('playsinline', '');   // iOS opens fullscreen without it
        video.muted = true;
        view.appendChild(video);

        var status = element('p', 'anatomy-camera-status', 'Starting the camera…');
        view.appendChild(status);

        // Relabelled by the failure path below: "Stop the camera" is the wrong
        // sentence next to a camera that never started.
        var stop = element('button', 'anatomy-layer', 'Stop the camera');
        stop.type = 'button';
        stop.addEventListener('click', stopCamera);
        view.appendChild(stop);

        intake.appendChild(view);
        camera.view = view;

        var button = byId(CAMERA);
        if (button) { button.setAttribute('aria-expanded', 'true'); }

        // `environment` is a preference, not a demand: a laptop has only one
        // camera and an exact constraint would fail outright rather than use it.
        navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } }, audio: false,
        }).then(function (stream) {
            camera.stream = stream;
            video.srcObject = stream;
            return video.play();
        }).then(function () {
            status.textContent = 'Point the camera at a code. It reads as soon as it sees one.';

            var frame = document.createElement('canvas');
            var context = frame.getContext('2d', { willReadFrequently: true });

            // Ten times a second rather than every animation frame: a phone
            // decoding sixty frames a second gets hot and gains nothing.
            camera.timer = window.setInterval(function () {
                if (!video.videoWidth) { return; }

                frame.width = video.videoWidth;
                frame.height = video.videoHeight;
                context.drawImage(video, 0, 0);

                var pixels = context.getImageData(0, 0, frame.width, frame.height);
                if (!jsQR(pixels.data, pixels.width, pixels.height)) { return; }

                // **The frame is frozen BEFORE the analysis.** Handing the live
                // video to takeApart would analyse whatever the camera happened
                // to be showing a moment later, which is not the frame that was
                // detected and may be a blur of the phone being lowered.
                stopCamera();
                takeApart(frame);
            }, 100);
        }).catch(function (error) {
            // **A FAILURE IS A WARNING CARD, NOT A CAPTION UNDER AN EMPTY
            // FRAME.** Owner's call, 2026-08-26. The first version left the
            // black video box in place and put the reason under it in the same
            // faint grey used for "Point the camera at a code" - so the largest
            // thing on screen was a picture of nothing, and the one piece of
            // information the reader now needs was the quietest thing on the
            // page. At this moment the message IS the content.
            //
            // The video and the caption are removed rather than hidden: there
            // is no camera, so there is nothing for them to show.
            if (camera.timer) { window.clearInterval(camera.timer); camera.timer = null; }

            video.remove();
            status.remove();

            var warning = element('div', 'anatomy-camera-warning', cameraMessage(error));
            warning.setAttribute('role', 'alert');
            view.insertBefore(warning, view.firstChild);

            stop.textContent = 'Close';
        });
    }

    document.addEventListener('click', function (event) {
        var button = event.target.closest && event.target.closest('#' + CAMERA);
        if (!button || button.disabled) { return; }
        if (camera.stream || camera.view) { stopCamera(); return; }
        startCamera();
    });

    document.addEventListener('visibilitychange', function () {
        if (document.hidden) { stopCamera(); }
    });

    window.addEventListener('pagehide', stopCamera);

    // -----------------------------------------------------------------------
    // THE X-RAY
    // -----------------------------------------------------------------------

    function themeColours() {
        // Read on every paint rather than cached, so switching theme and
        // re-rendering picks up the new tokens without a special case.
        var style = getComputedStyle(document.documentElement);
        return {
            dark: style.getPropertyValue('--color-base-content').trim() || '#000',
            light: style.getPropertyValue('--color-base-100').trim() || '#fff',
            accent: style.getPropertyValue('--color-primary').trim() || '#06f',
            pixels: 640,
        };
    }

    function buildXray(container, found, pixels) {
        var grid = sampleGrid(pixels.data, pixels.width, pixels.height,
            found.location, found.version);

        // **A grid that does not check out is not drawn.** The finder patterns
        // are the same shape in every QR code ever made and the timing lines
        // alternate by definition, so a sampled grid can be measured against
        // something known. Below the bar the transform is wrong and everything
        // drawn from it would be a plausible fiction. Reported rather than
        // thrown: the honest answer is that the picture was too poor.
        var confidence = gridLooksRight(grid.modules, grid.size);
        if (confidence < 0.92) { return null; }

        var classified = classify(found.version);
        var path = dataPath(found.version);

        var figure = element('figure', 'anatomy-xray');

        // **Controls above the code**, so the thing they change is below the
        // hand that changes it rather than scrolled past it.
        var controls = element('div', 'anatomy-controls');
        figure.appendChild(controls);

        var canvas = document.createElement('canvas');
        canvas.className = 'anatomy-canvas';
        canvas.setAttribute('aria-hidden', 'true');
        figure.appendChild(canvas);

        var caption = element('figcaption', 'anatomy-caption');
        figure.appendChild(caption);

        var state = { highlight: null, tracing: false, progress: 0 };
        var timer = null;
        var replay = null;

        function draw() {
            renderXray(canvas, grid, classified.roles, path, state, themeColours());
        }

        function say(text) { caption.textContent = text; }

        function stopTracing() {
            if (timer) { window.clearInterval(timer); timer = null; }
            if (replay) { window.clearTimeout(replay); replay = null; }
        }

        function press(button) {
            Array.prototype.forEach.call(controls.children, function (other) {
                other.setAttribute('aria-pressed', String(other === button));
            });
        }

        // **Reduced motion is not optional here**: this is a moving line, and
        // the alternative, the whole path at once, is just as informative.
        var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        var TRACE_CAPTION = 'A reader walks the squares in two-square columns, right '
            + 'to left, up then down, stepping over the timing line. That order is '
            + 'fixed, so every reader in the world walks yours the same way.';

        function trace() {
            stopTracing();
            state.highlight = null;
            state.tracing = true;
            say(TRACE_CAPTION);

            if (still) { state.progress = 1; draw(); return; }

            state.progress = 0;
            draw();
            timer = window.setInterval(function () {
                state.progress += 0.012;

                if (state.progress >= 1) {
                    state.progress = 1;
                    stopTracing();

                    // **IT REPEATS, because the run takes about a second and a
                    // half and the reader may not have been looking.** The code
                    // lands from a drop or a camera, which puts the answer under
                    // the reader's eye and this figure below it - so the one
                    // showing of the thing the page is actually about can easily
                    // happen off screen. A second's pause between runs is what
                    // separates them; without it the line appears to jump back
                    // to the start rather than to begin again.
                    replay = window.setTimeout(function () {
                        replay = null;
                        if (state.tracing) { trace(); }
                    }, 1000);
                }

                draw();
            }, 16);
        }

        // **NOT WHILE NOBODY CAN SEE IT**, which is the same reason it repeats
        // at all: the point is to be watched. A canvas redrawn sixty times a
        // second forever, in a tab somebody left open, is exactly the waste this
        // project measures elsewhere - and pausing it costs nothing, because a
        // figure that is off screen or in a hidden tab is not being missed, it
        // is not being looked at.
        function watchVisibility() {
            if (!window.IntersectionObserver) { return; }

            new IntersectionObserver(function (entries) {
                if (!entries[0].isIntersecting) {
                    stopTracing();
                    return;
                }

                // Resume from the start rather than mid-line: a run joined
                // halfway reads as a glitch rather than as an explanation.
                if (state.tracing && !timer) { trace(); }
            }, { threshold: 0.2 }).observe(canvas);
        }

        // **First, and selected when the code lands.** The reading order is the
        // thing worth seeing; the layers are for somebody who then wants to
        // know what the squares it walks past are.
        var watch = element('button', 'anatomy-layer',
            still ? 'How it is read' : 'Watch how it is read');
        watch.type = 'button';
        watch.setAttribute('aria-pressed', 'true');
        watch.addEventListener('click', function () { press(watch); trace(); });
        controls.appendChild(watch);

        layers().forEach(function (layer) {
            var button = element('button', 'anatomy-layer', layer.name);
            button.type = 'button';
            button.setAttribute('aria-pressed', 'false');
            button.addEventListener('click', function () {
                stopTracing();
                state.tracing = false;
                state.highlight = layer.role;
                press(button);
                say(layer.why);
                draw();
            });
            controls.appendChild(button);
        });

        container.appendChild(figure);

        // After the figure is in the document, so the observer has something
        // laid out to measure.
        watchVisibility();

        trace();
        return confidence;
    }

    // A bitmap, a canvas and a <video> are all drawable, and all three arrive
    // here: the file paths hand over the first, the camera the third. Only the
    // way they report their size differs.
    function sizeOf(source) {
        return {
            width: source.videoWidth || source.width,
            height: source.videoHeight || source.height,
        };
    }

    function takeApart(bitmap) {
        var container = byId(RESULT);
        if (!container) { return; }

        container.hidden = false;
        container.textContent = '';

        var size = sizeOf(bitmap);
        var canvas = document.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;

        var context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(bitmap, 0, 0);

        var pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        var found = jsQR(pixels.data, pixels.width, pixels.height);

        if (!found) { failure(container, size); return; }

        // ---- the answer, which is what the page is for ----------------------
        var described = describe(found.data);

        var answer = element('div', 'anatomy-answer');
        answer.appendChild(element('div', 'anatomy-kind', described.kind));
        answer.appendChild(element('div', 'anatomy-target', described.target || found.data));

        var fields = fieldList('anatomy-fields', described.fields);
        if (fields.childNodes.length) { answer.appendChild(fields); }

        container.appendChild(answer);

        // ---- and everything about HOW, which nobody has to read -------------
        var geek = element('section', 'anatomy-geek');
        geek.appendChild(element('h2', null, 'How this code is built'));
        geek.appendChild(element('p', 'anatomy-geek-intro',
            'None of this changes what the code does, and you can ignore all of '
            + 'it. It is here because the way those squares store that answer is '
            + 'genuinely interesting, and because the numbers say how big the '
            + 'code has to be printed before a phone stops reading it.'));

        var side = modulesPerSide(found.version);
        var plates = element('div', 'anatomy-plates');

        plates.appendChild(fieldList('anatomy-plate', [
            ['Version', String(found.version), 'versions'],
            ['Squares per side', String(side), 'ruler-measure'],
            ['Squares in total', String(side * side), 'grid-dots'],
        ]));

        plates.appendChild(fieldList('anatomy-plate', [
            ['Stored', found.binaryData.length + ' bytes', 'database'],
            ['Characters', String(found.data.length), 'letter-case'],
            ['Mode', modesOf(found.chunks), 'stack-2'],
        ]));

        container.appendChild(geek);

        // **The x-ray sits between the intro and the numbers.** It is the thing
        // worth looking at, so the plates read as captions to it rather than as
        // a wall to get past first.
        var confidence = buildXray(geek, found, pixels);

        geek.appendChild(plates);

        if (confidence === null) {
            geek.appendChild(offerToReport(bitmap, found));
        }
    }

    // -----------------------------------------------------------------------
    // WHEN THE DIAGRAM IS REFUSED
    //
    // **The reader is asked, and nothing is sent unless they press it.** This
    // page tells people the reading happens in their browser, so a quiet upload
    // would make the one claim it leads with false. The wording on the page and
    // in its metadata says "unless you send it to us" for the same reason.
    //
    // **Why ask at all**: every synthetic degradation tried on 2026-08-30 -
    // rotation, noise, resolution, lighting gradients, versions 2 to 33 -
    // failed to reproduce a single refusal, while the first real code anybody
    // pointed at it was refused. The picture is the only evidence that closes
    // that gap, and it cannot be manufactured here.
    // -----------------------------------------------------------------------

    function diagnostics(found) {
        var size = found.version ? (found.version * 4) + 17 : 0;
        var corners = found.location ? [
            found.location.topLeftCorner, found.location.topRightCorner,
            found.location.bottomRightCorner, found.location.bottomLeftCorner,
        ].map(function (p) { return Math.round(p.x) + ',' + Math.round(p.y); }).join('  ') : '';

        return [
            'The code read correctly, and the square by square diagram was refused.',
            '',
            'version:      ' + found.version + '  (' + size + ' modules per side)',
            'corners:      ' + corners,
            'payload:      ' + String(found.data).slice(0, 200),
            'screen:       ' + window.innerWidth + 'x' + window.innerHeight,
            'agent:        ' + navigator.userAgent,
        ].join('\n');
    }

    function offerToReport(bitmap, found) {
        var panel = element('div', 'anatomy-report');

        panel.appendChild(element('h3', 'anatomy-report-title',
            'We could not map this code square by square'));

        panel.appendChild(element('p', null,
            'The answer above is right. Reading a code and mapping every square are '
            + 'different jobs, and the second one did not come out cleanly here.'));

        panel.appendChild(element('p', null,
            'Codes that do this are exactly what we need to see, and we have not been '
            + 'able to make one on purpose. If you can spare a moment, send us this '
            + 'picture and it goes to one person who is trying to fix it.'));

        var button = element('button', 'anatomy-report-send', 'Send this picture to help fix it');
        button.type = 'button';

        var outcome = element('p', 'anatomy-report-outcome');

        button.addEventListener('click', function () {
            button.disabled = true;
            outcome.textContent = 'Sending\u2026';

            send(bitmap, found).then(function () {
                button.remove();
                outcome.textContent = 'Sent. Thank you, this is genuinely useful.';
            }).catch(function () {
                button.disabled = false;
                outcome.textContent = 'That did not send. Nothing was uploaded.';
            });
        });

        panel.appendChild(button);
        panel.appendChild(outcome);

        panel.appendChild(element('p', 'anatomy-report-note',
            'It sends the picture above and nothing else about you. Nothing is sent '
            + 'unless you press the button.'));

        return panel;
    }

    function send(bitmap, found) {
        // **JPEG, and re-encoded rather than forwarded.** A camera frame is
        // already lossy, the endpoint refuses anything larger than a frame, and
        // the payload rides to the worker as base64 in a database row.
        var canvas = document.createElement('canvas');
        var size = sizeOf(bitmap);
        canvas.width = size.width;
        canvas.height = size.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);

        return new Promise(function (resolve, reject) {
            canvas.toBlob(function (blob) {
                if (!blob) { reject(); return; }

                var form = new FormData();
                form.append('image', blob, 'anatomy.jpg');
                form.append('details', diagnostics(found));

                window.fetch('/tessera/anatomy/report', { method: 'POST', body: form })
                    .then(function (response) { response.ok ? resolve() : reject(); })
                    .catch(reject);
            }, 'image/jpeg', 0.82);
        });
    }

    function fromBlob(blob) {
        if (!blob) { return; }
        if (typeof createImageBitmap === 'function') {
            createImageBitmap(blob).then(takeApart).catch(function () { });
            return;
        }
        // Older Safari has no createImageBitmap for blobs. An <img> and an
        // object URL get to the same place, and the URL is revoked either way
        // so a dropped file cannot leak for the life of the page.
        var url = URL.createObjectURL(blob);
        var image = new Image();
        image.onload = function () { URL.revokeObjectURL(url); takeApart(image); };
        image.onerror = function () { URL.revokeObjectURL(url); };
        image.src = url;
    }

    // -----------------------------------------------------------------------
    // Drag and drop. `dragover` must be cancelled or the browser navigates to
    // the file instead, which looks exactly like the page crashing.
    // -----------------------------------------------------------------------
    function swapDropClass(zone, on) {
        var idle = zone.getAttribute('data-idle-class');
        var active = zone.getAttribute('data-active-class');
        if (!idle || !active) { return; }
        zone.classList.remove(on ? idle : active);
        zone.classList.add(on ? active : idle);
    }

    document.addEventListener('dragover', function (event) {
        var zone = event.target.closest && event.target.closest('#' + DROP);
        if (!zone) { return; }
        event.preventDefault();
        swapDropClass(zone, true);
    });

    document.addEventListener('dragleave', function (event) {
        var zone = event.target.closest && event.target.closest('#' + DROP);
        if (zone) { swapDropClass(zone, false); }
    });

    document.addEventListener('drop', function (event) {
        var zone = event.target.closest && event.target.closest('#' + DROP);
        if (!zone) { return; }
        event.preventDefault();
        swapDropClass(zone, false);
        var files = event.dataTransfer && event.dataTransfer.files;
        if (files && files.length) { fromBlob(files[0]); }
    });

    // Paste anywhere on the page. Someone who has just screenshotted a code
    // reaches for the keyboard before they reach for a file picker.
    document.addEventListener('paste', function (event) {
        var items = event.clipboardData && event.clipboardData.items;
        if (!items) { return; }
        for (var i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image/') === 0) {
                fromBlob(items[i].getAsFile());
                return;
            }
        }
    });

    document.addEventListener('change', function (event) {
        if (!event.target || event.target.id !== FILE) { return; }
        var files = event.target.files;
        if (files && files.length) { fromBlob(files[0]); }
    });

    // -----------------------------------------------------------------------
    // The camera, revealed only where it exists and asked for only on a click.
    //
    // **A denied permission is sticky.** Prompting somebody who did not ask does
    // not annoy them once, it disables the feature for them permanently, and
    // there is no friendly way back. So the button is the prompt.
    //
    // It also stays hidden without `getUserMedia`, and on an insecure origin,
    // where the call rejects rather than being absent.
    // -----------------------------------------------------------------------
    function cameraIsPossible() {
        return !!(window.isSecureContext
            && navigator.mediaDevices
            && typeof navigator.mediaDevices.getUserMedia === 'function');
    }

    // -----------------------------------------------------------------------
    // **THE STATE IS APPLIED WHENEVER THE BUTTON APPEARS, NOT ONCE AT LOAD.**
    //
    // The first version enabled it on `DOMContentLoaded` and again on a timer,
    // which worked on a cold load and failed on every navigation BACK to this
    // page: Blazor re-renders the markup without firing `DOMContentLoaded`,
    // and the timer had long since run. The button came back disabled and only
    // a full reload fixed it. Same family as I-81 and I-83, one step along: not
    // a listener attached to an orphan, but a one-shot that never runs again.
    //
    // An observer rather than another timer, because the honest trigger is "the
    // markup changed", and that is the thing being observed. The work is a flag
    // check on one element, so firing often costs nothing.
    // -----------------------------------------------------------------------
    function applyCameraState() {
        var button = byId(CAMERA);
        if (!button || button.dataset.cameraChecked === '1') { return; }

        button.dataset.cameraChecked = '1';

        if (cameraIsPossible()) {
            button.disabled = false;
            button.removeAttribute('title');
            return;
        }

        button.disabled = true;
        button.title = window.isSecureContext
            ? 'This browser does not offer camera access to a web page.'
            : 'The camera needs a secure connection, so it is unavailable over plain http.';
    }

    document.addEventListener('DOMContentLoaded', applyCameraState);
    applyCameraState();

    if (typeof MutationObserver === 'function') {
        new MutationObserver(applyCameraState)
            .observe(document.documentElement, { childList: true, subtree: true });
    }
})();
